import { Tooltip, Text, TextProps } from "@mantine/core";
import { debounce } from "lodash";
import { useRef, useState, useLayoutEffect } from "react";

interface OverflownTextProps extends TextProps {
  overTooltip?: string;
  /**
   * Suppresses this component's own tooltip. For a caller that already wraps a
   * strip of these in one tooltip listing every full label — `ReleaseChips` —
   * where the per-item tooltip would otherwise open nested inside it.
   */
  disableTooltip?: boolean;
}

export function OverflownText({
  children,
  overTooltip,
  disableTooltip = false,
  ...props
}: OverflownTextProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const [isOverflown, setIsOverflown] = useState(false);

  // Helper to check if a value is a valid React child
  function isRenderable(val: any) {
    return (
      typeof val === 'string' ||
      typeof val === 'number' ||
      (val && typeof val === 'object' && (val.$$typeof || Array.isArray(val)))
    );
  }

  // Warn if children or overTooltip are not renderable
  let safeChildren = children;
  let safeTooltip = overTooltip;
  if (!isRenderable(children)) {
    console.error('OverflownText: Invalid children passed (must be string, number, or React element):', children);
    safeChildren = '[Invalid value]';
  }
  if (overTooltip && !isRenderable(overTooltip)) {
    console.error('OverflownText: Invalid overTooltip passed (must be string, number, or React element):', overTooltip);
    safeTooltip = '[Invalid tooltip]';
  }

  useLayoutEffect(() => {
    const measure = debounce(() => {
      const element = ref.current;
      if (element) {
        setIsOverflown(
          element.clientWidth < element.scrollWidth ||
            element.offsetHeight < element.scrollHeight
        );
      }
    }, 100);

    measure();
    return () => measure.cancel();
  }, [safeChildren]);

  return (
    <Tooltip
      sx={{ whiteSpace: "break-spaces" }}
      label={safeTooltip || safeChildren}
      disabled={disableTooltip || (!isOverflown && !safeTooltip)}
      maw="50vw"
      multiline
      withinPortal
    >
      <Text
        color="#212529"
        ref={ref}
        sx={{
          whiteSpace: "nowrap",
          textOverflow: "ellipsis",
          overflow: "hidden",
          ...props.sx,
        }}
        {...props}
        fz={props.fz || 13.2}
      >
        {safeChildren}
      </Text>
    </Tooltip>
  );
}
